import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { fetchPut } from "app/utils/request";
import { UserModel } from "app/models";
import closeIcon from "app/images/close-icon.svg";

import styles from "./styles.module.scss";

interface EditContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: UserModel;
  facilityId: string;
  onSuccess?: (updatedContact: Partial<UserModel>) => void;
}

interface ContactFormData {
  firstName: string;
  lastName: string;
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
}

const useContactForm = (initialContact: UserModel, t: any) => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: initialContact.firstName || "",
    lastName: initialContact.lastName || "",
  });

  const [errors, setErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    setFormData({
      firstName: initialContact.firstName || "",
      lastName: initialContact.lastName || "",
    });
    setErrors({});
  }, [initialContact.id, initialContact.firstName, initialContact.lastName]);

  const updateField = useCallback(
    (field: keyof ContactFormData, value: string) => {
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };

        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };

          const noFirst = !updated.firstName.trim();
          const noLast = !updated.lastName.trim();

          if (noFirst && noLast) {
            newErrors.firstName = t("At least one name is required");
          } else {
            delete newErrors.firstName;
            delete newErrors.lastName;
          }

          return newErrors;
        });

        return updated;
      });
    },
    [],
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.firstName.trim() && !formData.lastName.trim()) {
      newErrors.firstName = t("At least one name is required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const hasChanges = useMemo(() => {
    return (
      formData.firstName !== (initialContact.firstName || "") ||
      formData.lastName !== (initialContact.lastName || "")
    );
  }, [formData, initialContact]);

  const hasErrors = useMemo(() => {
    return Object.keys(errors).length > 0;
  }, [errors]);

  return {
    formData,
    errors,
    hasChanges,
    hasErrors,
    updateField,
    validateForm,
    setErrors,
  };
};

const EditContactModal: React.FC<EditContactModalProps> = ({
  isOpen,
  onClose,
  contact,
  facilityId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const { formData, errors, hasChanges, hasErrors, updateField, validateForm } =
    useContactForm(contact, t);

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        contact: {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
        },
      };

      await fetchPut(
        `/facilities/${facilityId}/contacts/${contact.id}`,
        payload,
      );

      onSuccess?.({
        ...contact,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      });

      onClose();
    } catch (error) {
      console.error("Failed to update contact: ", error);
    } finally {
      setIsLoading(false);
    }
  }, [validateForm, formData, contact, onSuccess, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleSave();
      } else if (event.key === "Escape") {
        event.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  if (!contact?.id) {
    return null;
  }

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      classNames={styles.edit_contact_modal}
      wrapperClassName={styles.modal}
    >
      <div className={styles.modal_container} onKeyDown={handleKeyDown}>
        <header className={styles.modal_header}>
          <h1 className={styles.title} id="modal-title">
            {t("Edit Contact")}
          </h1>
          <img
            src={closeIcon}
            alt=""
            className={styles.close_icon}
            onClick={handleCancel}
          />
        </header>

        <main className={styles.modal_content} role="main">
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            noValidate
          >
            <div className={styles.input_group}>
              <label htmlFor="first_name" className={styles.label}>
                {t("First Name")}
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.firstName}
                onChange={(e) => updateField("firstName", e.target.value)}
                className={`${styles.input} ${errors.firstName ? styles.input_error : ""}`}
                disabled={isLoading}
                autoComplete="given-name"
              />
              {errors.firstName && (
                <span
                  id="first_name_error"
                  className={styles.error_text}
                  role="alert"
                >
                  {t(errors.firstName)}
                </span>
              )}
            </div>

            <div className={styles.input_group}>
              <label htmlFor="last_name" className={styles.label}>
                {t("Last Name")}
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.lastName}
                onChange={(e) => updateField("lastName", e.target.value)}
                className={`${styles.input} ${errors.lastName ? styles.input_error : ""}`}
                disabled={isLoading}
                autoComplete="family-name"
              />
              {errors.lastName && (
                <span
                  id="last_name_error"
                  className={styles.error_text}
                  role="alert"
                >
                  {t(errors.lastName)}
                </span>
              )}
            </div>
          </form>
        </main>

        <footer className={styles.modal_footer}>
          <ButtonVariant
            title={t("Cancel")}
            buttonType="secondary-outline"
            onClickFunc={handleCancel}
            disabled={isLoading}
          />
          <ButtonVariant
            title={isLoading ? t("Saving...") : t("Save")}
            buttonType="primary"
            onClickFunc={handleSave}
            disabled={isLoading || !hasChanges || hasErrors}
          />
        </footer>
      </div>
    </ModalLayout>
  );
};

export default EditContactModal;
